import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import type { Environment } from "../../../types";
import type { TokenInfo, TokenInfoResponse } from "../types";
import { getApiBase } from "../../../lib/remote-config";

export const TOKEN_INFO_QUERY_KEY = "token-info";

function buildCategoryKey(chainId: number, address: string): string {
  return `${address.toLowerCase()}_${chainId}`;
}

export function useTokenInfo(env: Environment, enabled = true) {
  const { data, ...rest } = useQuery<TokenInfoResponse>({
    queryKey: [TOKEN_INFO_QUERY_KEY, env],
    queryFn: async ({ signal }) => {
      const baseUrl = await getApiBase("walletServer", env);
      const res = await fetch(`${baseUrl}/v1/public/token/info`, {
        cache: "no-store",
        signal,
      });
      if (!res.ok) {
        throw new Error(`Failed to fetch token info: ${res.status}`);
      }
      const json: TokenInfoResponse = await res.json();
      if (json.code !== 200) {
        throw new Error(`Token info API error: ${json.code}`);
      }
      return json;
    },
    enabled,
    staleTime: Infinity,
  });

  const categoryMap = useMemo(() => {
    const map = new Map<string, string>();
    if (!data?.data) return map;
    for (const info of data.data) {
      const key = buildCategoryKey(info.chain_id, info.address);
      map.set(key, info.category);
    }
    return map;
  }, [data]);

  // 토큰 상세화면(소셜 링크·배포 여부 등)에서 단건 조회하기 위한 전체 메타 맵.
  const infoMap = useMemo(() => {
    const map = new Map<string, TokenInfo>();
    if (!data?.data) return map;
    for (const info of data.data) {
      map.set(buildCategoryKey(info.chain_id, info.address), info);
    }
    return map;
  }, [data]);

  return { categoryMap, infoMap, ...rest };
}

export { buildCategoryKey };
